import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Accordion } from '@mui/material';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  LocationAlertParamAlertMethodsInner,
  SensorWiFiConfOptionsAnalysisTypeEnum,
  Threshold,
  UIOptions,
} from '../../services/api';

import RemoveDialog from '../Dialog/RemoveDialog';
import { AlertParam } from './helpers';
import AnalysisOptionsContainer, {
  AnalysisOptions,
} from './AnalysisOptions/AnalysisOptionsContainer';
import AlertMethodsFormSection from './AlertMethodsForm';
import { VarName, varNameDetails } from '../../utils/varNames';
import SensorIcon from '../../styles/icons/SensorIcon';
import UIOptionsFormSection from './UIOptionsFormSection';
import { themeProps } from '../../styles/theme';

export interface AlertParamAccordionProps {
  checkListId: string;
  alertParam: AlertParam;
  isLocation: boolean;
  onSave: (alertParam: AlertParam) => void;
  onDelete: (alertId: number) => void;
  onUpdate: (alertId: number, alertParam: AlertParam) => void;
  onChangeParam: (patch: Partial<AlertParam>) => void;
  onChangeAnalysis: (patch: Partial<AnalysisOptions>) => void;
  changeStatus: (value: AlertParam) => void;
  cancelHandler: (value: number) => void;
}

export default function AlertParamAccordion({
  checkListId,
  alertParam,
  isLocation,
  onSave,
  onDelete,
  onUpdate,
  onChangeParam,
  onChangeAnalysis,
  changeStatus,
  cancelHandler,
}: AlertParamAccordionProps) {
  const theme = useTheme();

  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { alertStatus, alertId, analysisOptions } = alertParam;
  const { analysisType } = analysisOptions;

  const isWifiConf = analysisType === SensorWiFiConfOptionsAnalysisTypeEnum.SensorWiFiConf;

  const varName = isWifiConf ? VarName.ClientsWiFi : analysisOptions.varName;
  const threshold = !isWifiConf ? (analysisOptions.threshold as Threshold | undefined) : undefined;

  const { icon, label, metric } = varName
    ? varNameDetails[varName]
    : { icon: SensorIcon, label: '', metric: '' };

  const Icon = icon ?? SensorIcon;

  const hasExistingAlert = Boolean(alertStatus);
  const hasActiveAlert = alertStatus?.isActive === true;
  const allowEditing = !hasExistingAlert || isEditing;

  const labelSummary = (() => {
    const parts: string[] = [];

    if (threshold?.minValue !== undefined) {
      parts.push(`below ${threshold.minValue} ${metric ?? ''}`.trim());
    }

    if (threshold?.maxValue !== undefined) {
      parts.push(`above ${threshold.maxValue} ${metric ?? ''}`.trim());
    }

    if (threshold?.minRange !== undefined) {
      parts.push(`min range ${threshold.minRange}`);
    }

    if (threshold?.maxRange !== undefined) {
      parts.push(`max range ${threshold.maxRange}`);
    }

    if (parts.length === 0) {
      return '';
    }

    if (parts.length === 1) {
      return parts[0];
    }

    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  })();

  const handleRemove = () => {
    onDelete(alertId);
    setShowRemoveDialog(false);
  };

  const handleEditSave = () => {
    if (isEditing) {
      onUpdate(alertId, alertParam);
    }

    setIsEditing((current) => !current);
  };

  const handleNewAlertCancel = () => {
    cancelHandler(alertId);
  };

  return (
    <Accordion
      key={alertId}
      variant="outlined"
      defaultExpanded={!hasExistingAlert}
      sx={{
        my: 2,
        border: `solid 1px ${theme.palette.primary.contrastText}`,
        background: theme.palette.primary.main,
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="subtitle2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Icon color={hasActiveAlert ? 'success' : 'error'} />
          {hasExistingAlert ? '' : 'Creating'} Alert rules for{' '}
          {isWifiConf ? 'Wifi Configuration' : `${label} for`} {labelSummary}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <RemoveDialog
          showDialog={showRemoveDialog}
          title={`Alert Param on ${label} for ${labelSummary}`}
          removeHandler={handleRemove}
          cancelHandler={() => setShowRemoveDialog(false)}
        />

        <AnalysisOptionsContainer
          checkListId={checkListId}
          isLocation={isLocation}
          value={alertParam.analysisOptions}
          onChange={onChangeAnalysis}
          allowEditing={allowEditing}
        />

        <AlertMethodsFormSection
          value={alertParam.alertMethods}
          onChange={(methods: LocationAlertParamAlertMethodsInner[]) =>
            onChangeParam({ alertMethods: methods })
          }
          allowEditing={allowEditing}
        />

        <UIOptionsFormSection
          uiOptions={alertParam.uiOptions}
          onChange={(uiOptions: UIOptions) => onChangeParam({ uiOptions })}
          allowEditing={allowEditing}
        />

        <Box
          sx={{
            mt: 2,
            ml: 2,
            display: 'flex',
            gap: 1,
          }}
        >
          {!hasExistingAlert && (
            <>
              <Button
                onClick={() => onSave(alertParam)}
                variant={themeProps.btnVariant.secondary}
                color="success"
                startIcon={<SaveIcon />}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              >
                Save
              </Button>

              <Button
                onClick={handleNewAlertCancel}
                variant={themeProps.btnVariant.secondary}
                color="error"
                startIcon={<CloseIcon />}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              >
                Cancel
              </Button>
            </>
          )}

          {hasExistingAlert && (
            <>
              <Button
                onClick={handleEditSave}
                variant={themeProps.btnVariant.secondary}
                color="success"
                startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              >
                {isEditing ? 'Save' : 'Edit'}
              </Button>

              {isEditing ? (
                <Button
                  onClick={() => setIsEditing(false)}
                  variant={themeProps.btnVariant.secondary}
                  color="error"
                  startIcon={<CloseIcon />}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                >
                  Cancel
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => changeStatus(alertParam)}
                    variant={themeProps.btnVariant.secondary}
                    color="warning"
                    startIcon={<NotificationsIcon />}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {hasActiveAlert ? 'Snooze' : 'Enable Alerts'}
                  </Button>

                  <Button
                    onClick={() => setShowRemoveDialog(true)}
                    variant={themeProps.btnVariant.secondary}
                    color="error"
                    startIcon={<DeleteIcon />}
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: 'none',
                          sm: 'inline',
                        },
                      }}
                    >
                      Delete
                    </Box>
                  </Button>
                </>
              )}
            </>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
